
import { useState, useEffect } from "react";
import { getArticleBySlug, getArticleBySlugSync, ArticleData } from "@/data/articles/index";

export function useArticleLoader(slug: string | undefined) {
  const [article, setArticle] = useState<ArticleData | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  
  // Get article metadata synchronously for immediate access
  const articleMeta = slug ? getArticleBySlugSync(slug) : undefined;
  
  useEffect(() => {
    console.log("useArticleLoader - Starting article load for slug:", slug);
    
    const loadArticle = async () => {
      if (!slug) {
        console.error("useArticleLoader - No slug provided");
        setLoading(false);
        setLoadError("No article slug provided");
        return;
      }
      
      setLoading(true);
      setLoadError(null);
      
      try {
        console.log("useArticleLoader - Fetching full article for slug:", slug);
        
        // Special handling for harmful-content-detection
        if (slug === 'harmful-content-detection') {
          console.log("useArticleLoader - Special handling for harmful-content-detection article");
          
          // Create article data if it doesn't exist
          const harmfulArticleData: ArticleData = articleMeta || {
            id: 100,
            slug: "harmful-content-detection",
            title: "Harmful Content Detection",
            author: "HelloML",
            publishDate: "May 8, 2023",
            lastUpdated: "May 8, 2025",
            category: "ML System Design",
            readTime: "12 min read",
            readCount: 9758,
            likeCount: 478,
            isPremium: true,
            contentType: "markdown",
            description: "Learn how to design a system for detecting harmful content on social media platforms.",
            tags: ["machine learning", "content moderation", "system design"]
          };
          
          // Try to get content from the markdown file
          try {
            // Try multiple paths
            const possiblePaths = [
              '/docs/harmful-content-detection.md',
              'docs/harmful-content-detection.md',
              '/harmful-content-detection.md',
              '/assets/harmful-content-detection.md',
              '/articles/harmful-content-detection.md'
            ];
            
            let contentLoaded = false;
            
            for (const path of possiblePaths) {
              try {
                const response = await fetch(path);
                if (response.ok) {
                  const mdContent = await response.text();
                  harmfulArticleData.content = mdContent;
                  setArticle(harmfulArticleData);
                  setLoading(false);
                  contentLoaded = true;
                  console.log(`useArticleLoader - Successfully loaded harmful-content-detection from ${path}`);
                  return;
                }
              } catch (pathError) {
                console.warn(`useArticleLoader - Failed to load from path: ${path}`);
              }
            }
            
            if (!contentLoaded) {
              // If no content could be loaded, use the fallback content
              import('@/hooks/content-processor/utils/fallbackContent')
                .then((module) => {
                  const fallbackContent = module.getFallbackContent('harmful-content-detection');
                  harmfulArticleData.content = fallbackContent;
                  setArticle(harmfulArticleData);
                  setLoading(false);
                })
                .catch((error) => {
                  console.error("Failed to load fallback content:", error);
                  setLoadError("Failed to load article content");
                  setLoading(false);
                });
              return;
            }
          } catch (fetchError) {
            console.error("useArticleLoader - Error fetching markdown:", fetchError);
          }
        }
        
        // Regular article loading path
        const fullArticle = await getArticleBySlug(slug);
        
        console.log("useArticleLoader - Loaded article:", {
          title: fullArticle?.title,
          hasContent: fullArticle?.content ? true : false,
          contentLength: fullArticle?.content ? fullArticle.content.length : 0,
          contentType: fullArticle?.contentType,
          isPremium: fullArticle?.isPremium,
          slug,
          contentPath: fullArticle?.contentPath
        });
        
        if (!fullArticle) {
          console.error(`Article not found for slug: ${slug}`);
          setLoadError(`Article not found for: ${slug}`);
        } else if (!fullArticle.content) {
          console.error(`Content could not be loaded for article: ${slug}`);
          setLoadError("Content could not be loaded. Please try again later.");
        } else if (typeof fullArticle.content === 'string' && 
                  fullArticle.content.includes('export default') && 
                  fullArticle.content.includes('.html')) {
          // Handle the export default error case
          console.error(`Article content appears to be a module reference: ${fullArticle.content}`);
          setLoadError("Error loading article content. Please try again later.");
        }
        
        setArticle(fullArticle);
      } catch (error) {
        console.error("Failed to load article:", error);
        setLoadError(`Failed to load article content: ${error instanceof Error ? error.message : String(error)}`);
      } finally {
        setLoading(false);
      }
    };
    
    loadArticle();
  }, [slug, articleMeta]);

  return {
    article,
    articleMeta,
    loading,
    loadError
  };
}
